"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getVariablizedValue = exports.getVariablizedDeclarationBlock = exports.getVariablizedDeclaration = exports.getVariableName = exports.getCssVariables = exports.createVariablizedCss = exports.CSS_VARIABLE_MAP = void 0;
var _isNil = _interopRequireDefault(require("lodash/isNil"));
var _isArray = _interopRequireDefault(require("lodash/isArray"));
var _selectors = require("./selectors");
// this is a map of css variables that are used/actually defined.
// it will get populated as the css is generated, and can be used to
// output a list of all the variables that are used in the css
const CSS_VARIABLE_MAP = exports.CSS_VARIABLE_MAP = {};
const getVariableName = ({
  variablePrefix,
  variableName,
  blockName
}) => {
  if (!variableName) throw new Error('variableName is required to get a variable name');
  const prefixPart = variablePrefix ? `${variablePrefix}-` : '';
  const blockNamePart = blockName ? `${blockName}-` : '';
  return `${prefixPart}${blockNamePart}${variableName}`;
};
exports.getVariableName = getVariableName;
const getVariablizedValue = ({
  variablePrefix,
  variableName,
  value,
  blockName,
  isImportant,
  savedVariableNames
}) => {
  // TODO: error handling for missing variablePrefix or variableName

  const fullName = getVariableName({
    variablePrefix,
    variableName,
    blockName
  });
  if (!(0, _isNil.default)(savedVariableNames) && (0, _isArray.default)(savedVariableNames) && !savedVariableNames.includes(fullName)) {
    savedVariableNames.push(fullName);
    // sort the array alphabetically
    savedVariableNames.sort();
  }
  return `var(--${fullName}, ${value})${isImportant ? ' !important' : ''}`;
};

/**
 * @param {string} variablePrefix - The prefix to use for the variable name
 * @param {string} property - The css property to variablize
 * @param {string} value - The value to use as the default
 * @param {string} blockName - The name of the block to use in the variable name
 * @param {boolean} isImportant - Whether to use the !important flag
 * @param {string[]} savedVariableNames - An array to push the variable names to
 *
 * @returns {string} - The css declaration with the variable
 */
exports.getVariablizedValue = getVariablizedValue;
const getVariablizedDeclaration = ({
  variablePrefix,
  property,
  value,
  blockName,
  isImportant,
  savedVariableNames
}) => {
  return `${property}: ${getVariablizedValue({
    variablePrefix,
    variableName: property,
    value,
    blockName,
    isImportant,
    savedVariableNames
  })};`;
};

/**
 * @param {string} variablePrefix - The prefix to use for the variable name
 * @param {string} blockName - The name of the block to use in the variable name
 * @param {string[]} selectors - The css selectors to apply the declarations to
 * @param {object[]} declarations - The css declarations to variablize
 * @param {boolean} applyToElementDirectly - Whether to apply the styles directly to the element, via prepending '&'
 * @param {string[]} savedVariableNames - An array to push the variable names to
 *
 * @returns {string} - The css block with the selectors and declarations
 *
 * NOTE: doesn't format the output, so it's all on one line
 *
 */
exports.getVariablizedDeclaration = getVariablizedDeclaration;
const getVariablizedDeclarationBlock = ({
  variablePrefix,
  blockName,
  selectors,
  declarations,
  applyToElementDirectly = false,
  savedVariableNames
}) => {
  if (!selectors || !(0, _isArray.default)(selectors) || selectors.length === 0) {
    if (_selectors.selectorGroups[blockName]) selectors = _selectors.selectorGroups[blockName];else throw new Error('selectors is required');
  }
  if (!declarations || !(0, _isArray.default)(declarations) || declarations.length === 0) throw new Error('declarations is required');
  // NOTE: could do some defaulting here, set blockName to first selector?
  // prob better to just require it
  if (!blockName) throw new Error('blockName is required');
  const variableDeclarations = declarations.map(({
    property,
    value,
    isImportant
  }) => {
    return getVariablizedDeclaration({
      variablePrefix,
      property,
      value,
      blockName,
      isImportant,
      savedVariableNames
    });
  });

  // Only show the hover colors on devices supporting hover. Without this,
  // hover state will stick on mobile devices... this logic splits out the :hover
  // selectors into a separate block that only applies on hover-supporting devices
  const selectorGroup = (0, _selectors.getSelectorGroup)({
    selectors,
    opts: {
      applyToElementDirectly,
      returnListInsteadOfString: selectors.some(selector => selector.includes(_selectors.SELECTOR_HOVER))
    }
  });
  if ((0, _isArray.default)(selectorGroup)) {
    // create hover css block, remove hover from selectors
    const hoverSelectorsString = selectorGroup.filter(selector => selector.includes(_selectors.SELECTOR_HOVER)).join(', ');
    const nonHoverSelectorGroupString = selectorGroup.filter(selector => !selector.includes(_selectors.SELECTOR_HOVER)).join(', ');
    return `${nonHoverSelectorGroupString} {
            ${variableDeclarations.join(' ')}
        }
        @media (hover: hover) {
          ${hoverSelectorsString} {
              ${variableDeclarations.join(' ')}
          }
        }`.replaceAll(/\s+/g, ' ');
  }
  return `${selectorGroup} {
            ${variableDeclarations.join(' ')}
        }`.replaceAll(/\s+/g, ' ');
};
exports.getVariablizedDeclarationBlock = getVariablizedDeclarationBlock;
const createVariablizedCss = ({
  variablePrefix,
  variableName,
  property,
  value,
  isImportant,
  selectors,
  declarations,
  blockName,
  applyToElementDirectly,
  // for getting list of used variables
  outputFilepath
}) => {
  if (blockName && !(selectors || declarations)) {
    throw new Error('selectors and declarations are required when using blockName');
  }
  if (!blockName && !property && !variableName) {
    throw new Error('if not using blockName, property or variableName is required');
  }
  let savedVariableNames = [];
  if (CSS_VARIABLE_MAP[variablePrefix]) {
    savedVariableNames = CSS_VARIABLE_MAP[variablePrefix];
  } else {
    CSS_VARIABLE_MAP[variablePrefix] = savedVariableNames;
  }
  const variablizedCss = blockName ? getVariablizedDeclarationBlock({
    variablePrefix,
    blockName,
    selectors,
    declarations,
    applyToElementDirectly,
    savedVariableNames
  }) : variableName ? getVariablizedValue({
    variablePrefix,
    variableName,
    value,
    blockName,
    isImportant,
    savedVariableNames
  }) : getVariablizedDeclaration({
    variablePrefix,
    property,
    value,
    blockName,
    savedVariableNames,
    isImportant
  });
  return variablizedCss;
};
exports.createVariablizedCss = createVariablizedCss;
const getCssVariables = () => {
  // sort the variable names alphabetically
  Object.keys(CSS_VARIABLE_MAP).forEach(key => {
    CSS_VARIABLE_MAP[key].sort();
  });

  // sort the keys alphabetically
  const sortedKeys = Object.keys(CSS_VARIABLE_MAP).sort();
  // create a new object with the sorted keys
  const sortedCssVariableMap = {};
  sortedKeys.forEach(key => {
    sortedCssVariableMap[key] = CSS_VARIABLE_MAP[key];
  });
  return sortedCssVariableMap;
};
exports.getCssVariables = getCssVariables;